import { useState, useCallback, useEffect, useRef } from 'react'
import { motion } from 'motion/react'
import { RefreshCcw, Plus, List, Layers } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { FollowupFilter, FollowupViewMode, Followup } from './types'
import { fetchFollowups, createLoop, resolveLoop, snoozeLoop, updateLoopDueAt } from './api'
import { useExpandable } from '../../shared/hooks/useExpandable'
import { useProfession } from './useProfession'
import { getGroupLabelKey } from './utils'
import { BackButton } from '@tendbot/ui'
import { SkeletonListItem } from '@tendbot/ui'
import { defaultStagger } from '@tendbot/ui'
import { LoopCard, LoopFilterBar, LoopEmptyState, CreateLoopSheet, GroupedView } from './components'

interface Props {
  onBack?: () => void
}

export default function FollowupsScreen({ onBack }: Props) {
  const { t } = useTranslation('followups')
  const [activeFilter, setActiveFilter] = useState<FollowupFilter>('open')
  const [viewMode, setViewMode] = useState<FollowupViewMode>('flat')
  const expandState = useExpandable()
  const profession = useProfession()

  const [items, setItems] = useState<Followup[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)

  // Track current filter to avoid stale responses
  const filterRef = useRef(activeFilter)
  // eslint-disable-next-line react-hooks/refs -- sync ref for async callback staleness check
  filterRef.current = activeFilter

  const loadItems = useCallback((filter: FollowupFilter) => {
    const currentFilter = filter
    setLoading(true)
    setError(null)
    setItems([])

    fetchFollowups(currentFilter)
      .then(res => {
        if (filterRef.current !== currentFilter) return
        setItems(res)
      })
      .catch(err => {
        if (filterRef.current !== currentFilter) return
        setError(err instanceof Error ? err.message : 'Failed to load loops')
      })
      .finally(() => {
        if (filterRef.current !== currentFilter) return
        setLoading(false)
      })
  }, [])

  useEffect(() => {
    loadItems(activeFilter)
  }, [activeFilter, loadItems])

  const handleFilter = useCallback((f: FollowupFilter) => {
    setActiveFilter(f)
    expandState.collapse()
  }, [expandState])

  const handleCreate = useCallback(async (data: { title: string; context?: string; dueAt?: string; groupName?: string }) => {
    const loop = await createLoop(data)
    // Refresh the list to show new item in correct position
    setItems(prev => [loop, ...prev])
  }, [])

  const handleResolve = useCallback(async (id: string) => {
    const updated = await resolveLoop(id)
    setItems(prev => prev.map(l => l.id === id ? updated : l))
  }, [])

  const handleSnooze = useCallback(async (id: string, until: string) => {
    const updated = await snoozeLoop(id, until)
    setItems(prev => prev.map(l => l.id === id ? updated : l))
  }, [])

  const handleUpdateDueAt = useCallback(async (id: string, dueAt: string | null) => {
    const updated = await updateLoopDueAt(id, dueAt)
    setItems(prev => prev.map(l => l.id === id ? updated : l))
  }, [])

  const { container: containerVariants, item: itemVariants } = defaultStagger

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="h-full overflow-y-auto px-5 sm:px-8 lg:px-12 py-4"
    >
      <div className="max-w-2xl mx-auto">
        {/* Back button (shown when accessed as sub-screen, e.g. desktop) */}
        {onBack && <BackButton onClick={onBack} />}

        {/* Header */}
        <motion.div variants={itemVariants} className="mb-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center">
              <RefreshCcw size={20} className="text-gold" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-lg text-ivory font-semibold">{t('title')}</h2>
              {!loading && items.length > 0 && (
                <p className="text-pearl/60 text-xs">
                  {items.filter(l => l.status === 'open').length} {t('openCount')}
                </p>
              )}
            </div>
            {/* View toggle */}
            <button
              onClick={() => setViewMode(v => v === 'flat' ? 'grouped' : 'flat')}
              className="w-8 h-8 rounded-lg bg-slate-mid/30 flex items-center justify-center text-pearl/60 hover:text-ivory hover:bg-slate-mid/50 transition-colors"
              title={viewMode === 'flat'
                ? t('viewMode.grouped', { label: t(getGroupLabelKey(profession)) })
                : t('viewMode.flat')}
            >
              {viewMode === 'flat' ? <Layers size={16} /> : <List size={16} />}
            </button>
          </div>
        </motion.div>

        {/* Filter chips */}
        <motion.div variants={itemVariants} className="mb-5 border-b border-gold/10">
          <LoopFilterBar active={activeFilter} onFilter={handleFilter} />
        </motion.div>

        {/* Loading state */}
        {loading && (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonListItem key={i} />
            ))}
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-8">
            <p className="text-rose-400 text-sm">{error}</p>
          </motion.div>
        )}

        {/* Empty state */}
        {!loading && !error && items.length === 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <LoopEmptyState filtered={activeFilter !== 'open'} />
          </motion.div>
        )}

        {/* Loop list */}
        {!loading && items.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {viewMode === 'grouped' ? (
              <GroupedView
                items={items}
                expandedId={expandState.expandedId}
                onToggle={(id) => expandState.toggle(id)}
                onResolve={handleResolve}
                onSnooze={handleSnooze}
                onUpdateDueAt={handleUpdateDueAt}
              />
            ) : (
              <div className="space-y-2">
                {items.map(loop => (
                  <LoopCard
                    key={loop.id}
                    loop={loop}
                    isExpanded={expandState.isExpanded(loop.id)}
                    onToggle={() => expandState.toggle(loop.id)}
                    onResolve={handleResolve}
                    onSnooze={handleSnooze}
                    onUpdateDueAt={handleUpdateDueAt}
                  />
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Bottom spacer */}
        <div className="h-24" />
      </div>

      {/* FAB — Create Loop */}
      <button
        onClick={() => setShowCreate(true)}
        className="fixed bottom-20 right-5 lg:bottom-8 lg:right-8 w-14 h-14 rounded-full bg-gold text-midnight shadow-lg shadow-gold/20 flex items-center justify-center hover:bg-gold-light transition-colors z-30"
      >
        <Plus size={24} />
      </button>

      {/* Create Loop Sheet */}
      <CreateLoopSheet
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreate={handleCreate}
        groupLabel={t('create.groupLabel')}
      />
    </motion.div>
  )
}
